/**
 * usePersistedState - A hook that persists state to localStorage
 *
 * Use this for admin dashboard state that should survive:
 * - Navigation between views
 * - Page refreshes
 * - Browser tab close/reopen
 * - Days between sessions
 *
 * Keys are namespaced with "admin:" prefix to avoid collisions.
 */

import { useState, useCallback } from "react";

/**
 * Custom hook that syncs state with localStorage.
 *
 * @param key - Storage key (will be prefixed with namespace)
 * @param defaultValue - Default value if nothing stored
 * @returns [value, setValue] tuple like useState
 *
 * @example
 * const [selectedId, setSelectedId] = usePersistedState<string | null>(
 *   "admin:entries:professional",
 *   null
 * );
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  // Initialize state from localStorage or use default
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      return JSON.parse(stored) as T;
    } catch {
      // If parsing fails (corrupted data), return default
      return defaultValue;
    }
  });

  // Wrapper that updates both state and localStorage
  const setPersistedState = useCallback(
    (value: T) => {
      setState(value);
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch {
        // localStorage might be full or disabled - state still works
        console.warn(`Failed to persist state for key: ${key}`);
      }
    },
    [key]
  );

  return [state, setPersistedState];
}
